<template>
  <div class="pb-2">
    <active-filters />
    <b-overlay :show="showOverlay">
      <b-card no-body no-footer>
        <b-card-body class="p-0">
          <b-table-simple class="mb-0" responsive :busy="showOverlay">
            <b-thead>
              <b-tr>
                <b-th class="bg-primary d-flex text-white text-capitalize" sticky-column
                  :style="{ height: '60px !important' }">
                  <span>{{ $t('table.name') }}</span>
                  <table-header-sort sort-label="NAME" :sort-options="sortOptions"
                    @update:sort-options="options => $emit('update:sort-options', options)" />
                </b-th>
                <b-th class="bg-primary text-white text-capitalize">
                  <div class="d-flex">
                    <span>{{ $t('Total') }}</span>
                    <table-header-sort v-if="canEditPayment" sort-label="BILLABLE_AMOUNT" :sort-options="sortOptions"
                      @update:sort-options="options => $emit('update:sort-options', options)" />
                  </div>
                </b-th>
                <b-th v-for="wa of weekArray" :key="wa.key" class="bg-primary text-white text-capitalize text-nowrap"
                  :class="{ 'today-column': isToday(wa.key) }">
                  {{ wa.text }}
                  <span class="d-block cursor-pointer" :class="isAmountBlur ? 'blurry-text' : ''"
                    @click="setIsAmountBlur(!isAmountBlur)">
                    ({{ totalDailySpendings[wa.key] ? formatCurrency(totalDailySpendings[wa.key]) : formatCurrency(0)
                    }})
                  </span>
                </b-th>
              </b-tr>
            </b-thead>
            <b-tbody>
              <b-tr v-for="row of rows" :key="row.uuid" :set="activities = row.dailyActivities.data">
                <b-td class="px-0" sticky-column>
                  <div class="d-flex">
                    <b-avatar class="ml-1 mt-1" badge :src="getAvatar(row.avatar)"
                      :badge-variant="row.isOnline === true ? 'success' : 'secondary'" badge-offset="-0.2em"
                      :text="getInitials(row.name)" :size="42" :variant="getRandomVariant()" />
                    <div class="mt-1 font-weight-bold">
                      <span class="text-nowrap m-0 ml-1 p-0">{{ IS_MOBILE() ? FORMAT_NAME(row.name) : row.name }}</span>
                      <h5 v-if="activities.length > 0" class="pl-1 nickname text-success mt-0">
                        {{ activities[activities.length - 1].date }}
                      </h5>
                      <h6 v-else class="pl-1 small text-muted mt-0">
                        {{ $t('reports.never-tracked-time') }}
                      </h6>
                    </div>
                  </div>
                </b-td>
                <b-td>
                  <div class="d-flex flex-column cursor-pointer">
                    <strong :class="isAmountBlur ? 'blurry-text' : ''" @click="setIsAmountBlur(!isAmountBlur)">
                      {{ formatCurrency(getTotalSpend(row, activities)) }}
                    </strong>
                    <small class="text-muted">{{ getTotalTimeWorked(activities) }}</small>
                  </div>
                </b-td>
                <b-td v-for="wa, i of weekArray" :key="wa.key" :set="workingTime = getWorkingTime(activities, wa.key)"
                  :class="{ 'today-column': isToday(wa.key) }">
                  <span @click="SHOW_RECORD(wa.key, row.uuid)">
                    <b-tooltip :target="`${row.uuid}_${wa.key}`">
                      <span v-if="workingTime.rawTime > 0">
                        <feather-icon icon="ClockIcon" />
                        {{ workingTime.time }}
                      </span>
                    </b-tooltip>
                    <div :id="`${row.uuid}_${wa.key}`" class="day-hours-cell">
                      <div class="day-hours-badge" :class="getDayHoursBadgeClass(workingTime.rawTime)">
                        <span v-if="workingTime.rawTime > 0" class="hours-text"
                          :class="isAmountBlur ? 'blurry-text' : ''" @click="toggleAmountBlur">{{
                            formatCurrency(getAmountInfo(row.dailySpendings.data, wa.key).amount) }}</span>
                      </div>
                    </div>
                  </span>
                </b-td>
              </b-tr>
            </b-tbody>
            <b-tfoot v-if=!rows.length>
              <b-tr>
                <b-td colspan="9" class="text-center">
                  {{ $t('No Data Found') }}
                </b-td>
              </b-tr>
            </b-tfoot>
          </b-table-simple>
        </b-card-body>
      </b-card>
      <div class="w-100 centralize">
        <b-pagination v-if="totalItems > perPage" v-model="currentPage" :total-rows="totalItems" :per-page="perPage"
          first-number last-number align="right" prev-class="prev-item" next-class="next-item" class="mt-1 mb-0"
          @input="getUsers">
          <template #prev-text>
            <feather-icon icon="ChevronLeftIcon" size="18" />
          </template>
          <template #next-text>
            <feather-icon icon="ChevronRightIcon" size="18" />
          </template>
        </b-pagination>
      </div>
    </b-overlay>
  </div>
</template>
<script>

import {
  BTableSimple,
  BTr,
  BTd,
  BTh,
  BThead,
  BTbody,
  BTfoot,
  BCard,
  BCardBody,
  BAvatar,
  BProgress,
  BProgressBar,
  VBTooltip,
  BPagination,
  BTooltip,
  BOverlay,
  BBadge,
} from 'bootstrap-vue'
import useApollo from '@/plugins/graphql/useApollo'
import { setComponentReady } from '@/views/tour/tour'
import { showOverlay } from '@/views/tour/tourHelper'
import { mapActions, mapGetters } from 'vuex'
import TableHeaderSort from '@/views/common/components/TableHeaderSort.vue'
import useFilters from '@/views/common/components/Filters/useFilters'
import ActiveFilters from '@/views/common/components/Filters/ActiveFilters.vue'

const moment = require('moment-timezone')

export default {
  components: {
    BTableSimple,
    BTr,
    BTd,
    BThead,
    BTbody,
    BTfoot,
    BTh,
    BCard,
    BCardBody,
    BAvatar,
    BProgress,
    BProgressBar,
    BPagination,
    BTooltip,
    BOverlay,
    BBadge,
    TableHeaderSort,
    ActiveFilters,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    searchDate: {
      type: String,
      required: true,
    },
    selectedTimezone: {
      type: [String, Object],
      default: null,
    },
    searchTerm: {
      type: Array,
      default: () => [],
    },
    sortOptions: {
      type: Object,
      default: () => ({
        sortBy: 'BILLABLE_AMOUNT',
        sortDirection: 'DESC',
      }),
    },
    setTotalWeeklySpending: {
      type: Function,
      default: () => { },
    },
    setIsAmountBlur: {
      type: Function,
      default: () => { },
    },
    isAmountBlur: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      moment,
      rows: [],
      currentPage: 1,
      perPage: 10,
      totalItems: 0,
      showOverlay: false,
      isMounted: false,
      totalDailySpendings: {},
    }
  },
  computed: {
    ...mapGetters('project', ['GET_USERS']),
    ...mapGetters('permissions', ['canEditPayment']),
    activeFilters() {
      const { getActiveFilters } = useFilters()
      return getActiveFilters.value
    },
    weekInterval() {
      const startDay = this.$store.state.project.company?.weekStartDay ?? 'SUNDAY'
      const weekStart = moment(this.searchDate).day(startDay)
      const weekEnd = moment(weekStart).add('7', 'days')
      return {
        start: weekStart,
        end: weekEnd,
      }
    },
    weekArray() {
      const startOfWeek = this.weekInterval.start
      const endOfWeek = this.weekInterval.end

      const days = []
      let day = startOfWeek

      while (day < endOfWeek) {
        days.push({
          text: day.format('ddd, MMM D'),
          key: day.format('Y-MM-DD'),
        })
        day = day.clone().add(1, 'd')
      }
      return days
    },
  },
  watch: {
    searchDate() {
      if (this.isMounted) this.getUsers()
    },
    selectedTimezone(newVal, oldVal) {
      if (this.isMounted && newVal !== oldVal) {
        this.getUsers()
      }
    },
    searchTerm: {
      handler() {
        if (this.isMounted) this.getUsers()
      },
      deep: true,
    },
    sortOptions: {
      handler() {
        if (this.isMounted) {
          this.currentPage = 1
          this.getUsers()
        }
      },
      deep: true,
    },
    activeFilters: {
      handler() {
        if (this.isMounted) {
          this.currentPage = 1
          this.getUsers()
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.getUsers()
    this.$nextTick(() => {
      this.isMounted = true
    })
  },
  methods: {
    ...mapActions('project', ['updateUsers', 'updateHasNextUsersPage']),
    getUsers() {
      const { sortBy, sortDirection } = this.sortOptions
      this.showOverlay = true
      useApollo.activities.getWeeklySpend({
        keyword: this.searchTerm && this.searchTerm.length ? this.searchTerm : null,
        page: this.currentPage,
        first: this.perPage,
        startTime: this.weekInterval.start.format('YYYY-MM-DD 00:00:00'),
        endTime: this.weekInterval.end.format('YYYY-MM-DD 00:00:00'),
        projectId: this.$store.state.project.selectedProject,
        sortBy,
        sortDirection,
        timezone: this.selectedTimezone,
        tags: this.activeFilters.tags?.map(tag => tag.name),
        ...(this.activeFilters.isOnline !== null ? { isOnline: this.activeFilters.isOnline } : {}),
        startOfMonth: moment().startOf('month').format('YYYY-MM-DD 00:00:00'),
        endOfMonth: moment().endOf('month').format('YYYY-MM-DD 23:59:59'),
      }).then(response => {
        // Extract project weekly spending (prefer aggregated project totals when available)
        if (response.data.project) {
          // prefer explicit total if provided by the query
          this.setTotalWeeklySpending(response.data.project.dailySpendingsTotal || response.data.project.activityAmount || 0)

          // Prefer project-level daily series (already aggregated across users). Fall back to users aggregation for older responses.
          const projectSeries = response.data.project.dailySpendingsSeries?.data || response.data.project.dailySpendings?.data
          if (projectSeries && Array.isArray(projectSeries)) {
            this.totalDailySpendings = projectSeries.reduce((acc, obj) => {
              acc[obj.date] = obj.amount || 0
              return acc
            }, {})
          } else {
            // Backward compatibility: sum per-user series into totals per date
            this.totalDailySpendings = (response.data.users?.data || [])
              .map(user => user.dailySpendings?.data)
              .flat()
              .reduce((acc, obj) => {
                acc[obj.date] = (acc[obj.date] || 0) + (obj.amount || 0)
                return acc
              }, {})
          }
        } else {
          this.totalDailySpendings = {}
        }

        this.rows = response.data.users.data.map(user => ({
          ...user,
        }))
        this.totalItems = response.data.users.total

        this.updateUsers(this.rows.map(({ name, uuid, avatar, tags, isOnline }) => ({
          name,
          text: name,
          value: uuid,
          avatar,
          tags,
          isOnline,
        }))).then(() => {
          if (this.GET_USERS.length === this.totalItems) this.updateHasNextUsersPage(false)
        })
      }).finally(() => {
        this.showOverlay = false
        this.$nextTick(() => {
          setComponentReady()
        })
      })
    },
    getTotalTime(duration, idle) {
      return duration * (1 - idle)
    },
    formatDayHours(seconds) {
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      if (hours === 0 && minutes === 0) return ''
      if (hours === 0) return `${minutes}m`
      return `${hours}h ${minutes}m`
    },
    getDaySpend(arr, day) {
      const findDay = arr.find(d => d.date === day)
      if (findDay) {
        const hours = findDay.duration / 3600
        const amount = (findDay.activityAmount || 0)
        return {
          amount,
          duration: this.CONVERT_HM(findDay.duration),
          rawDuration: findDay.duration,
        }
      }
      return {
        amount: 0,
        duration: '0h',
        rawDuration: 0,
      }
    },
    getTotalSpend(row, activities) {
      const limitType = row.projectSettings?.data?.[0]?.information?.type
      if (limitType === 'MONTHLY' && this.shouldShowLimit(limitType)) {
        // Use monthly duration for monthly budget
        return (row.activityAmount || 0)
      }
      // Use weekly total for WEEKLY budget or when not in current month
      return this.getWeeklySpend(activities, row)
    },
    getTotalTimeWorked(activities) {
      const totalSeconds = activities.reduce((i, v) => i + Number(v.duration), 0)
      return this.CONVERT_HM(totalSeconds)
    },
    getWorkingTime(arr, day) {
      const findDay = arr.find(d => d.date === day)
      const time = findDay ? findDay.duration : 0
      return {
        time: this.CONVERT_HM(time),
        rawTime: time,
        idle: findDay ? this.CONVERT_HM(time * findDay.idlePercent) : 0,
      }
    },
    getAmountInfo(arr, day) {
      const findDay = arr.find(d => d.date === day)
      const amount = findDay ? findDay.amount : 0
      return {
        amount
      }
    },
    formatDayHours(seconds) {
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      if (hours === 0 && minutes === 0) return ''
      if (hours === 0) return `${minutes}m`
      return `${hours}h ${minutes}m`
    },
    getDayHoursBadgeClass(seconds) {
      if (seconds === 0) return 'no-hours'
      const hours = seconds / 3600
      if (hours < 1) return 'warning-hours'
      return 'has-hours'
    },
    getTotalSpend(row, activities) {
      const limitType = row.projectSettings?.data?.[0]?.information?.type
      if (limitType === 'MONTHLY' && this.shouldShowLimit(limitType)) {
        return row.activityAmount || 0
      }
      // Use weekly total for WEEKLY budget or when not in current month
      return this.getWeeklySpend(activities, row)
    },
    getWeeklySpend(activities, row = null) {

      return row?.activityAmount || 0
    },
    formatCurrency(amount) {
      const currencySymbol = this.$store.state.project.company?.currency?.symbol || '$'
      return `${currencySymbol}${amount.toFixed(2)}`
    },
    isToday(date) {
      const timezone = this.selectedTimezone || 'UTC'
      const today = moment.tz(timezone).format('Y-MM-DD')
      return date === today
    },
    shouldShowLimit(limitType) {
      const now = moment()
      if (limitType === 'WEEKLY') {
        // Show for weekly budget only if the selected week includes today
        return now.isBetween(this.weekInterval.start, this.weekInterval.end, 'day', '[]')
      }
      // Show for monthly budget if the current week overlaps with the current month
      const currentMonthStart = now.clone().startOf('month')
      const currentMonthEnd = now.clone().endOf('month')

      // Check if the week interval overlaps with the current month
      return this.weekInterval.start.isSameOrBefore(currentMonthEnd, 'day')
        && this.weekInterval.end.isSameOrAfter(currentMonthStart, 'day')
    },
    toggleAmountBlur(event) {
      if (this.isAmountBlur) {
        event.stopPropagation()
        this.setIsAmountBlur(!this.isAmountBlur)
      }
    }
  },
}
</script>
<style scoped lang="scss">
.progress {
  border-radius: 0 !important;

  .progress-bar {
    border-radius: 0 !important;
  }
}

.day-hours-cell {
  cursor: pointer;
}

.day-hours-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 70px;
  height: 36px;
  border-radius: 6px;
  font-weight: 500;
  font-size: 14px;

  &.has-hours {
    background-color: rgba(40, 199, 111, 0.2);
    border: 2px solid rgba(40, 199, 111, 0.5);
    color: #28c76f;

    .hours-text {
      color: #28c76f;
    }
  }

  &.warning-hours {
    background-color: rgba(255, 159, 67, 0.2);
    border: 2px solid rgba(255, 159, 67, 0.5);
    color: #ff9f43;

    .hours-text {
      color: #ff9f43;
    }
  }

  &.no-hours {
    background-color: rgba(130, 134, 139, 0.15);
    border: 2px solid rgba(130, 134, 139, 0.3);
  }
}

.dark-layout {
  .day-hours-badge {
    &.has-hours {
      background-color: rgba(40, 199, 111, 0.25);
      border-color: rgba(40, 199, 111, 0.4);
    }

    &.warning-hours {
      background-color: rgba(255, 159, 67, 0.25);
      border-color: rgba(255, 159, 67, 0.4);
    }

    &.no-hours {
      background-color: rgba(130, 134, 139, 0.2);
      border-color: rgba(130, 134, 139, 0.35);
    }
  }
}

td.today-column {
  background-color: rgba(40, 199, 111, 0.1) !important;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background-color: #28c76f;
  }
}

.dark-layout .today-column {
  background-color: rgba(40, 199, 111, 0.15) !important;
}

.blurry-text {
  color: transparent !important;
  text-shadow: 0 0 7px rgba(0, 0, 0, 0.6);
  /* Use rgba for better transparency control */
}

.dark-layout {
  .blurry-text {
    text-shadow: 0 0 7px rgba(255, 255, 255, 0.6);
  }
}
</style>
